import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardBody, CardHeader } from '../components/ui';
import { NotificationModeSelector } from '../components/NotificationModeSelector';
import { LinkifiedText } from '../components/LinkifiedText';
import {
  listTags,
  markDiscussionNotificationsSeen,
  setDiscussionNotificationAllMode,
  setDiscussionNotificationTagMode,
  subscribeDiscussionNotificationSettings,
  subscribeTopics
} from '../lib/firestore';
import {
  effectiveTopicSummaryMode,
  DEFAULT_DISCUSSION_NOTIFICATION_SETTINGS
} from '../lib/notifications';
import { groupDiscussionTags, valueForDiscussionCategory, resolveDiscussionTopicTags } from '../lib/discussionCategories';
import { formatExactDateTime, formatRelativeDateTime } from '../lib/dates';
import { memberLabel, memberPublicProfileUrl, useMemberDirectory } from '../lib/memberDirectory';
import type { DiscussionTopic, Tag } from '../lib/types';
import { useAuth } from '../auth/AuthProvider';

function IconChevron(props: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={props.className}
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

export default function NotificationsPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const memberDirectory = useMemberDirectory();
  const [tags, setTags] = React.useState<Tag[]>([]);
  const [topics, setTopics] = React.useState<DiscussionTopic[]>([]);
  const [settings, setSettings] = React.useState(DEFAULT_DISCUSSION_NOTIFICATION_SETTINGS);
  const [categoriesOpen, setCategoriesOpen] = React.useState(false);

  React.useEffect(() => {
    listTags('discussion', true).then(setTags).catch(() => setTags([]));
  }, []);

  React.useEffect(() => {
    if (!user?.sub) return;
    return subscribeDiscussionNotificationSettings(user.sub, setSettings);
  }, [user?.sub]);

  React.useEffect(() => {
    if (!user?.sub) return;
    void markDiscussionNotificationsSeen(user.sub);
  }, [user?.sub]);

  React.useEffect(() => {
    const unsub = subscribeTopics({ onData: setTopics, take: 200 });
    return () => unsub();
  }, []);

  const relevantTopics = React.useMemo(() => {
    return topics
      .map((topic) => ({
        topic,
        mode: effectiveTopicSummaryMode(topic, settings)
      }))
      .filter((item) => item.mode !== 'off');
  }, [topics, settings]);

  const overriddenTags = React.useMemo(
    () => tags.filter((tag) => valueForDiscussionCategory(settings.tagModes, tag.id) === 'off'),
    [settings.tagModes, tags]
  );
  const categoryRows = React.useMemo(
    () => groupDiscussionTags(categoriesOpen ? tags : overriddenTags).flat(),
    [categoriesOpen, overriddenTags, tags]
  );
  const weeklySummaryMode = settings.allDiscussionsMode === 'off' ? 'off' : 'summary';

  return (
    <div className="grid grid-cols-1 gap-4">
      <Card>
        <CardHeader title="Notifications" />
        <CardBody className="space-y-3">
          <div className="overflow-hidden rounded-xl border border-zinc-200">
            <div className="flex items-center justify-between gap-4 px-3 py-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-zinc-900">Weekly summary email (Friday)</div>
              </div>
              <NotificationModeSelector
                value={weeklySummaryMode}
                onChange={(next) => {
                  if (!user?.sub || next === 'default' || next === 'immediate') return;
                  void setDiscussionNotificationAllMode(user.sub, next);
                }}
                options={['summary', 'off']}
                labels={{ summary: 'On', off: 'Off' }}
              />
            </div>

          </div>

          <div className="overflow-hidden rounded-xl border border-zinc-200">
            <button
              type="button"
              onClick={() => setCategoriesOpen((current) => !current)}
              className="flex w-full items-center justify-between gap-4 px-3 py-3 text-left hover:bg-zinc-50"
              aria-expanded={categoriesOpen}
            >
              <div className="min-w-0">
                <div className="text-sm font-semibold text-zinc-900">Category subscriptions</div>
              </div>
              <div className="flex items-center gap-2 text-xs font-medium text-zinc-600">
                <IconChevron className={`h-4 w-4 transition ${categoriesOpen ? 'rotate-180' : ''}`} />
              </div>
            </button>

            {categoryRows.length > 0 ? (
              <div className="border-t border-zinc-200">
                {categoryRows.map((tag) => (
                  <div
                    key={tag.id}
                    className="border-t border-zinc-200 px-3 py-3 first:border-t-0"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-zinc-900">{tag.name}</div>
                        {!categoriesOpen ? <div className="text-xs text-zinc-600">Category override</div> : null}
                      </div>
                      <NotificationModeSelector
                        value={valueForDiscussionCategory(settings.tagModes, tag.id) === 'off' ? 'off' : 'summary'}
                        onChange={(next) => {
                          if (!user?.sub || next === 'default' || next === 'immediate') return;
                          void setDiscussionNotificationTagMode(user.sub, tag.id, next === 'summary' ? null : 'off');
                        }}
                        options={['summary', 'off']}
                        size="sm"
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : tags.length === 0 ? (
              <div className="border-t border-zinc-200 px-3 py-3 text-sm text-zinc-600">No discussion categories</div>
            ) : null}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Recently updated Topics in your interest categories"
        />
        <CardBody className="space-y-3">
          {relevantTopics.map(({ topic }) => {
            const author = memberLabel(topic.authorProfileId, topic.authorDisplayName, memberDirectory);
            const topicTags = resolveDiscussionTopicTags(topic.tagIds, tags);
            return (
            <div
              key={topic.id}
              role="link"
              tabIndex={0}
              onClick={() => nav(`/discussion/${topic.id}`)}
              onKeyDown={(evt) => {
                if (evt.key === 'Enter' || evt.key === ' ') {
                  evt.preventDefault();
                  nav(`/discussion/${topic.id}`);
                }
              }}
              className="cursor-pointer rounded-xl border border-zinc-200 p-3 hover:bg-zinc-50"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="break-words text-sm font-semibold text-zinc-900">{topic.title}</div>
                  {topicTags.length ? (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {topicTags.map((tag) => (
                        <span
                          key={tag.id}
                          className="rounded-full border border-zinc-300 bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-700"
                        >
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="mt-2 line-clamp-2 text-sm text-zinc-700">
                <LinkifiedText text={topic.body} />
              </div>

              <div className="mt-2 flex items-center justify-between gap-2 text-xs text-zinc-600">
                <a
                  href={memberPublicProfileUrl(topic.authorProfileId)}
                  onClick={(evt) => evt.stopPropagation()}
                  className="truncate text-zinc-700 underline decoration-zinc-300 underline-offset-2 hover:text-zinc-900"
                  title={author}
                >
                  {author}
                </a>
                <span title={formatExactDateTime(topic.updatedAt || topic.lastActivityAt)}>
                  Updated {formatRelativeDateTime(topic.updatedAt || topic.lastActivityAt)}
                </span>
              </div>
            </div>
            );
          })}

          {relevantTopics.length === 0 ? (
            <div className="text-sm text-zinc-600">No discussion topics match your current notification settings.</div>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}
